import type { Evidence, ProcessUploadResponse } from 'api';

import type { NonNullableField } from '../types';

export enum PhotoRejectionReason {
  TOO_CLOSE = 'Material is too close',
  TOO_FAR = 'Material is too far away',
  BAD_FRAME = 'Angle is not flat',
  BAD_QUALITY = 'Photo is blurry or contains sun flair',
  LOW_RESOLUTION = 'Photo resolution is too low to be analysed correctly',
  LOW_QUALITY = 'Material is too compressed, dirty, or shredded',
  INVALID_FORMAT = 'File format must be .jpeg or .png',
  EMPTY_FILE = 'Failed to analyse as provided file is empty',
  DUPLICATE = 'Photo is duplicated',
  BAD_ASPECT_RATIO_REGULAR = 'Aspect ratio invalid, must be greater than 0.75 (4:3, 3:4, or 1:1)',
  BAD_ASPECT_RATIO_EXTENDED = 'Aspect ratio invalid, must be greater than 0.5625 (16:9, 4:3, 3:4, or 1:1)',
  TREERING = 'Cannot analyse the treering side',
  OVER_EXPOSED = 'Photo is too bright',
  UNDER_EXPOSED = 'Photo is too dark',
  AI_ANALYSIS_IS_NOT_AVAILABLE = 'AI analysis is not available',
  ANALYSIS_IS_NOT_AVAILABLE = 'Analysis is not available',
}

export enum PhotoWarningReason {
  TOO_FAR = 'Material is too far away',
  MODEL_MISMATCH = 'Wrong material type or form',
}

export type AnalysisStatus = 'accepted' | 'rejected-material' | 'rejected-photo' | 'retry' | 'warning';

export type Bale = {
  id: string;
  photo: File;
  /** AI analysis */
  analysis?: ProcessUploadResponse;
  isLoading?: boolean;
  /** AI analysis status */
  analysisStatus?: AnalysisStatus;
  loadingStatus?: 'loaded' | 'not-loaded'; // loading status should be taken out of the store - it's not central to evidence management
  uploadProgress?: number;
  photoRejectedReason?: PhotoRejectionReason;
  photoWarningReason?: PhotoWarningReason;
  /** available only if analyzed by AI, and online evidence created */
  evidenceId?: number;
};

export type BalePhotoEvidence = Evidence & {
  analysisStatus: AnalysisStatus;
  photoRejectedReason?: PhotoRejectionReason;
  photoWarningReason?: PhotoWarningReason;
  loadingStatus?: 'loaded' | 'not-loaded';
};

export type EvidenceWithModelResults = NonNullableField<Evidence, 'model_results'>;

export enum BaleModels {
  PET = 'pet',
  ALU = 'alu',
  BRIQUETTE = 'briquette',
}
export const baleModels = Object.values(BaleModels);

type BaseEvidence = {
  id: string;
  isLoading?: boolean;
};
export type NewEvidence = BaseEvidence & {
  variant: 'new';
  photo: string;
  uploadProgress?: number;
  photoRejectedReason?: PhotoRejectionReason;
  photoWarningReason?: PhotoWarningReason;
  analysisStatus?: AnalysisStatus;
  file: File;
};

export type OnlineEvidence = BaseEvidence & {
  variant: 'online';
  photo: string;
  analysis: ProcessUploadResponse | Evidence;
  loadingStatus?: 'loaded' | 'not-loaded';
  uploadProgress?: number;
  analysisStatus: AnalysisStatus;
  photoRejectedReason?: PhotoRejectionReason;
  photoWarningReason?: PhotoWarningReason;
  evidenceId: number;
};

export type OfflineEvidence = BaseEvidence & {
  variant: 'offline';
  photo: string;
};

export type MaterialEvidence = OnlineEvidence | OfflineEvidence | NewEvidence;

export const isNewEvidence = (evidence: MaterialEvidence): evidence is NewEvidence => evidence.variant === 'new';
export const isOnlineEvidence = (evidence: MaterialEvidence): evidence is OnlineEvidence =>
  evidence.variant === 'online';
export const isOfflineEvidence = (evidence: MaterialEvidence): evidence is OfflineEvidence =>
  evidence.variant === 'offline';
